import React, { ComponentProps, useContext, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import FormItem from 'components/utils/FormItem'
import Header from 'components/utils/Form/Header'
import Footer from 'components/utils/Form/Footer'
import { ItemWrapper, OffsetedContainer } from 'styles/common'
import { AppContext } from 'App'
import { FormContext } from 'components/utils/Form.context'
import { Form as FormHelper } from 'helpers/form'
import { Routing as RoutingHelper } from 'helpers/routing'
import { Col, Form } from 'react-bootstrap'
import FormCheck from 'components/utils/FormCheck'
import { MatchDatasourceIdentifierRecord } from 'data/MatchDatasourceIdentifierRecord'
import MatchDatasourceIdentifierContext from './MatchDatasourceIdentifier.context'

const SubmitKeys = [
  'admin_note',
  'identifier',
  'identifier_kind',
  'is_enabled',
  'match_id'
] as Array<keyof MatchDatasourceIdentifierRecord>

type ValidationKeys = Record<
  keyof Pick<MatchDatasourceIdentifierRecord, 'identifier' | 'identifier_kind'>,
  any
>

const MatchDatasourceIdentifierForm: React.FC<FormHelper.Props<MatchDatasourceIdentifierRecord> & {matchId?: string}> = (p) => {
  const { db, alert, history } = useContext(AppContext)
  const { params: mdi_params } = useContext(MatchDatasourceIdentifierContext)

  const [form, setForm] = useState<{
    data?: MatchDatasourceIdentifierRecord
    state: FormHelper.FormState
  }>({ data: p.data, state: p.state ?? 'new' })

  const opt = {
    alert: {
      manager: alert,
      texts: {
        form: 'Match Datasource Identifier',
      },
    },
    history: history,
    lock: {
      callback: p.locked?.callback,
    },
  }

  const formik = useFormik({
    initialValues: {
      ...form.data,

      id: form.data?.id,
      match_id: p.matchId
    },

    onSubmit: async (values) => {
      if (!db?.matchDatasourceIdentifiers) return

      try {
        values.id = form?.data?.id // TMP the form somehow loses its id

        const data = await FormHelper.submitChanges<MatchDatasourceIdentifierRecord>({
          action: form.state === 'new' ? 'create' : 'update',
          keys: SubmitKeys,
          data: Object.assign(new MatchDatasourceIdentifierRecord(), values ?? {}),
          repository: db.matchDatasourceIdentifiers,
          optional: opt,
        })

        if (data?.id) {
          setForm({ data: data, state: 'existing' })

          history?.replace(
            RoutingHelper.getMatchDatasourceIdentifierUrlFromMatch({
              match: mdi_params?.match_id ?? RoutingHelper.NewRecordSymbol,
              tournament: mdi_params?.tournament_id ?? RoutingHelper.NewRecordSymbol,
              matchDatasourceIdentifier: RoutingHelper.NewRecordSymbol
            })
          )
        }

        data && p.submitCallback?.(data)
      } catch (err) {
        console.error(err)
      }
    },

    validationSchema: Yup.object<ValidationKeys>({
      identifier: Yup.string().required('Identifier is a required field'),
      identifier_kind: Yup.string().required('Identifier_kind is a required field'),
    }),
  })

  const delete_ = async () => {
    if (!db?.matchDatasourceIdentifiers) return

    await FormHelper.delete_({
      data: Object.assign(new MatchDatasourceIdentifierRecord(), formik.values ?? {}),
      repository: db.matchDatasourceIdentifiers,
      optional: opt,
    })
  }

  //#region Form Components
  const _Identifier = (
    <FormItem
      label={<Form.Label>Identifier</Form.Label>}
      input={
        <>
          <Form.Control
            name={'identifier' as keyof MatchDatasourceIdentifierRecord}
            value={formik.values.identifier ?? ''}
            onChange={formik.handleChange}
            required
            isInvalid={formik.submitCount > 0 && !!formik.errors.identifier}
            readOnly={p.locked?.value}
          />
          <Form.Control.Feedback type={'invalid'}>
            {formik.errors.identifier}
          </Form.Control.Feedback>
        </>
      }
    />
  )

  const _IdentifierKind = (
    <FormItem
      label={<Form.Label>Identifier kind</Form.Label>}
      input={
        <>
          <Form.Control
            name={'ident' as keyof MatchDatasourceIdentifierRecord}
            value={formik.values.identifier_kind ?? ''}
            onChange={formik.handleChange}
            required
            isInvalid={formik.submitCount > 0 && !!formik.errors.identifier_kind}
            readOnly={p.locked?.value}
          />
          <Form.Control.Feedback type={'invalid'}>
            {formik.errors.identifier_kind}
          </Form.Control.Feedback>
        </>
      }
    />
  )

  const _IsEnabled = (
    <FormCheck
      name={'is_enabled' as keyof MatchDatasourceIdentifierRecord}
      value={formik.values.is_enabled ?? false}
      checked={formik.values.is_enabled ?? false}
      offset={{direction: 'left'}}
      title={'Is Enabled'}
      scheme={'green'}
      checkedCallback={() => {
        formik.setFieldValue('is_enabled' as keyof MatchDatasourceIdentifierRecord, true)
      }}
      uncheckedCallback={() => {
        formik.setFieldValue('is_enabled' as keyof MatchDatasourceIdentifierRecord, false)
      }}
    />
  )

  const _AdminNote = (
    <FormItem
      label={'admin_note' as keyof MatchDatasourceIdentifierRecord}
      input={
        <Form.Control
          name={'admin_note' as keyof MatchDatasourceIdentifierRecord}
          value={formik.values.admin_note ?? ''}
          onChange={formik.handleChange}
        />
      }
    />
  )
  //#endregion

  const _ComputeFooterProps = (): ComponentProps<typeof Footer> => {
    if (form.state === 'new')
      return {
        useSave: {},
      }

    return { useSave: {}, useDelete: {} }
  }

  return (
    <OffsetedContainer>
      <FormContext.Provider
        value={{
          submit: formik.submitForm,
          delete: delete_
        }}
      >
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Header />

          <Form.Row>
            <Col>
              <Form.Row>
                <Col as={ItemWrapper}>{_Identifier}</Col>
              </Form.Row>
              <Form.Row>
                <Col as={ItemWrapper}>{_IdentifierKind}</Col>
              </Form.Row>
            </Col>
            <Col>
              <Form.Row>
                <Col as={ItemWrapper}>{_IsEnabled}</Col>
              </Form.Row>
            </Col>
          </Form.Row>
          <Form.Row>
            <Col as={ItemWrapper}>{_AdminNote}</Col>
          </Form.Row>

          <Footer {..._ComputeFooterProps()} />
        </Form>
      </FormContext.Provider>
    </OffsetedContainer>
  )
}

export default MatchDatasourceIdentifierForm
